import { filter, map } from "rxjs";
import { TranslocoService } from "@jsverse/transloco";
import { inject, Injectable } from "@angular/core";
import { HttpClient, HttpStatusCode } from "@angular/common/http";

import { IApiResponse, IApiResponsePaged } from "@azm/core/interfaces/response";
import type { IURLConfig } from "@azm/core/interfaces/url-config";
import { loaderContext } from "@azm/core/utilities/loader-context";
import { toDirectPagedResponse } from "@azm/core/utilities/to-direct-paged-response";

export { systemLoader, loaderContext, silentLoaderContext } from "@azm/core/utilities/loader-context";

@Injectable({
	providedIn: "root",
})
export abstract class HttpService {
	readonly #http = inject(HttpClient);
	protected readonly translocoService = inject(TranslocoService);

	protected abstract get controllerName(): string;

	protected get<T>(URLConfig: IURLConfig) {
		return this.#http
			.get<IApiResponse<T>>(`${this.controllerName}${URLConfig.APIName}`, {
				params: URLConfig.params,
				responseType: URLConfig.responseType as "json",
				context: URLConfig.context ?? loaderContext("loader"),
				headers: {
					...URLConfig.headers,
					"show-alert": URLConfig.showAlert ? "true" : "false",
					"error-alert-message": URLConfig.errorAlertMessage || "",
					"success-alert-message": URLConfig.successAlertMessage || "",
				},
				withCredentials: URLConfig.withCredentials,
			})
			.pipe(
				map((event) => {
					if (URLConfig.responseType) {
						return {
							data: event as T,
							message: "",
							statusCode: HttpStatusCode.Ok,
							success: true,
							brokenRules: [],
							errors: [],
						} as IApiResponse<T>;
					}

					return event;
				}),
			)
			.pipe(
				filter((event) => !!event),
				map((event) => event.data),
			);
	}

	protected getPaged<T>(URLConfig: IURLConfig) {
		return this.#http
			.get<IApiResponse<IApiResponsePaged<T>>>(`${this.controllerName}${URLConfig.APIName}`, {
				params: URLConfig.params,
				context: URLConfig.context ?? loaderContext("loader"),
				headers: {
					...URLConfig.headers,
					"show-alert": URLConfig.showAlert ? "true" : "false",
					"error-alert-message": URLConfig.errorAlertMessage || "",
					"success-alert-message": URLConfig.successAlertMessage || "",
				},
			})
			.pipe(
				map((event) => {
					const directPaged = toDirectPagedResponse<T>(event);
					return directPaged ?? event;
				}),
			);
	}

	protected post<T>(URLConfig: IURLConfig) {
		return this.#http
			.post<IApiResponse<T>>(`${this.controllerName}${URLConfig.APIName}`, URLConfig.body, {
				params: URLConfig.params,
				responseType: URLConfig.responseType as "json",
				headers: {
					...URLConfig.headers,
					"show-alert": URLConfig.showAlert ? "true" : "false",
					"error-alert-message": URLConfig.errorAlertMessage || "",
					"success-alert-message": URLConfig.successAlertMessage || "",
				},

				context: URLConfig.context ?? loaderContext("loader"),
			})
			.pipe(
				map((event) => {
					if (URLConfig.responseType) {
						return event as T;
					}

					if (!event.data && event.statusCode !== HttpStatusCode.Ok) throw new Error(event.message);

					if (Array.isArray(event.data) && "pageNumber" in event) {
						return event as T;
					}

					return event.data;
				}),
			);
	}

	protected put<T>(URLConfig: IURLConfig) {
		return this.#http
			.put<IApiResponse<T>>(`${this.controllerName}${URLConfig.APIName}`, URLConfig.body, {
				params: URLConfig.params,
				context: URLConfig.context ?? loaderContext("loader"),
				headers: {
					...URLConfig.headers,
					"show-alert": URLConfig.showAlert ? "true" : "false",
					"error-alert-message": URLConfig.errorAlertMessage || "",
					"success-alert-message": URLConfig.successAlertMessage || "",
				},
			})
			.pipe(map((event) => event.data));
	}

	protected patch<T>(URLConfig: IURLConfig) {
		return this.#http
			.patch<IApiResponse<T>>(`${this.controllerName}${URLConfig.APIName}`, URLConfig.body, {
				params: URLConfig.params,
				headers: {
					...URLConfig.headers,
					"show-alert": URLConfig.showAlert ? "true" : "false",
					"error-alert-message": URLConfig.errorAlertMessage || "",
					"success-alert-message": URLConfig.successAlertMessage || "",
				},
			})
			.pipe(map((event) => event.data));
	}

	protected delete(URLConfig: IURLConfig) {
		return this.#http
			.delete<IApiResponse<boolean>>(`${this.controllerName}${URLConfig.APIName}`, {
				body: URLConfig.body,
				params: URLConfig.params,
				headers: {
					...URLConfig.headers,
					"show-alert": URLConfig.showAlert ? "true" : "false",
					"error-alert-message": URLConfig.errorAlertMessage || "",
					"success-alert-message": URLConfig.successAlertMessage || "",
				},
			})
			.pipe(map((event) => event.data));
	}

	protected postAttachment<T = { attachmentId: string }>(URLConfig: IURLConfig) {
		return this.#http.post<IApiResponse<T>>(`${this.controllerName}${URLConfig.APIName}`, URLConfig.body, {
			params: URLConfig.params,
			reportProgress: true,
			observe: "events",
			context: loaderContext("attachment"),
			headers: {
				...URLConfig.headers,
				"show-alert": URLConfig.showAlert ? "true" : "false",
				"error-alert-message": URLConfig.errorAlertMessage || "",
				"success-alert-message": URLConfig.successAlertMessage || "",
			},
		});
	}
}
